import React, { useState } from "react";
import { Badge, ErrorBox, Field, Options, UserOptions } from "./common";
import { errorMessage, fmtDate, nullIfBlank, numOrNull } from "../dmApi";

const EMPTY = { Content_Type: "Poster", Title: "", Body: "", Media_URL: "", Tool_Used: "", Prepared_By: "" };
const TOOLS = ["Canva", "Pollygen", "ChatGPT", "Gemini", "Photoshop", "CapCut", "Other"];

const ACTION_LABELS = {
  "Internal Review": "Send for internal review",
  "Client Review": "Send to client",
  Approved: "Approve",
  Rejected: "Reject",
  Draft: "Back to draft",
};

function ContentForm({ meta, initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...Object.fromEntries(Object.entries(initial || {}).map(([k, v]) => [k, v ?? ""])),
    Prepared_By: initial?.Prepared_By ? String(initial.Prepared_By) : "",
  }));
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      Content_Type: form.Content_Type,
      Title: form.Title.trim(),
      Body: nullIfBlank(form.Body),
      Media_URL: nullIfBlank(form.Media_URL.trim()),
      Tool_Used: nullIfBlank(form.Tool_Used),
      ...(form.Prepared_By ? { Prepared_By: numOrNull(form.Prepared_By) } : {}),
    });
  };

  return (
    <form onSubmit={submit} className="dmp-card">
      <div className="dmp-form-grid">
        <Field label="Type">
          <select value={form.Content_Type} onChange={set("Content_Type")}>
            <Options values={meta?.content_types} />
          </select>
        </Field>
        <Field label="Title *">
          <input required value={form.Title} onChange={set("Title")} placeholder="eastern-pride-poster-1" />
        </Field>
        <Field label="Tool used">
          <input list="dmp-tools" value={form.Tool_Used} onChange={set("Tool_Used")} />
          <datalist id="dmp-tools">
            {TOOLS.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </Field>
        <Field label="Prepared by">
          <select value={form.Prepared_By} onChange={set("Prepared_By")}>
            <UserOptions users={meta?.users} blank="Me" />
          </select>
        </Field>
        <Field label="Media URL (image / video / drive link)" full>
          <input value={form.Media_URL} onChange={set("Media_URL")} placeholder="https://…" />
        </Field>
        <Field label={form.Content_Type === "Keywords" ? "Keywords" : "Text / caption"} full>
          <textarea rows={4} value={form.Body} onChange={set("Body")} />
        </Field>
      </div>
      <div className="dmp-form-actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : "Save content"}
        </button>
      </div>
    </form>
  );
}

/** Content pipeline: Draft → Internal Review → Client Review → Approved (or Rejected). */
function ContentBoard({ api, meta, projectId, content, onChange }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const statuses = meta?.review_statuses || [];
  const transitions = meta?.review_transitions || {};

  const run = async (fn) => {
    setError("");
    try {
      await fn();
      await onChange();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    }
  };

  const save = async (body) => {
    setSaving(true);
    const ok = await run(() =>
      editing === "new" ? api.createContent(projectId, body) : api.updateContent(editing.Content_Id, body)
    );
    setSaving(false);
    if (ok) setEditing(null);
  };

  const move = (item, target) => {
    let notes;
    if (target === "Rejected") {
      notes = window.prompt("Why is this content rejected?");
      if (!notes) return;
    }
    run(() => api.reviewContent(item.Content_Id, { Review_Status: target, Review_Notes: notes }));
  };

  const remove = (item) => {
    if (window.confirm(`Delete "${item.Title}"?`)) run(() => api.deleteContent(item.Content_Id));
  };

  return (
    <>
      <div className="dmp-header" style={{ marginBottom: 10 }}>
        <p className="dmp-subtitle" style={{ margin: 0 }}>
          Review content internally first, then send it to the client for approval. Editing approved content sends it
          back to draft.
        </p>
        <button type="button" className="dmp-btn" onClick={() => setEditing("new")}>
          Add content
        </button>
      </div>
      <ErrorBox message={error} />
      {editing ? (
        <ContentForm
          key={editing === "new" ? "new" : editing.Content_Id}
          meta={meta}
          initial={editing === "new" ? null : editing}
          saving={saving}
          onSubmit={save}
          onCancel={() => setEditing(null)}
        />
      ) : null}
      <div className="dmp-board">
        {statuses.map((status) => {
          const items = content.filter((c) => c.Review_Status === status);
          return (
            <div key={status} className="dmp-board__col">
              <div className="dmp-board__head">
                <span>{status}</span>
                <span className="dmp-muted">{items.length}</span>
              </div>
              {items.map((item) => (
                <div key={item.Content_Id} className="dmp-item">
                  <div className="dmp-chips">
                    <Badge value="type">{item.Content_Type}</Badge>
                    {item.Tool_Used ? <span className="dmp-muted">{item.Tool_Used}</span> : null}
                  </div>
                  <div className="dmp-item__title">{item.Title}</div>
                  {item.Body ? <div className="dmp-item__body">{item.Body}</div> : null}
                  {item.Media_URL ? (
                    <a href={item.Media_URL} target="_blank" rel="noreferrer" className="dmp-muted">
                      View media
                    </a>
                  ) : null}
                  <div className="dmp-muted">
                    {item.Prepared_By_Name ? `By ${item.Prepared_By_Name}` : ""}
                    {item.Reviewed_By_Name ? ` · Reviewed ${item.Reviewed_By_Name}` : ""}
                    {item.Approved_On ? ` · Approved ${fmtDate(item.Approved_On)}` : ""}
                  </div>
                  {item.Review_Notes ? <div className="dmp-notice" style={{ margin: "6px 0 0", padding: 6 }}>{item.Review_Notes}</div> : null}
                  <div className="dmp-item__actions">
                    {(transitions[status] || []).map((target) => (
                      <button
                        key={target}
                        type="button"
                        className={`dmp-btn dmp-btn--sm${target === "Rejected" ? " dmp-btn--danger" : target === "Approved" ? "" : " dmp-btn--ghost"}`}
                        onClick={() => move(item, target)}
                      >
                        {ACTION_LABELS[target] || target}
                      </button>
                    ))}
                    <button type="button" className="dmp-link" onClick={() => setEditing(item)}>
                      Edit
                    </button>
                    <button type="button" className="dmp-link" style={{ color: "var(--dmp-danger)" }} onClick={() => remove(item)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default ContentBoard;
